import React, { useContext, useState, useRef, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { useTenant } from '../context/TenantContext';
import { useDarkMode } from '../context/DarkModeContext';
import { Menu, X, Home, Utensils, Grid3x3, ShoppingCart, Package, Heart, Users, BarChart3, LogOut, Settings, Shield, ShieldAlert, Zap, Sun, Moon, ChevronDown, MessageCircle, PieChart, Puzzle } from 'lucide-react';
import SubscriptionBanner from './SubscriptionBanner';
import CookieBanner from './CookieBanner';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
];

const Layout = () => {
  const { user, logout } = useContext(AuthContext);
  const { tenant, isFeatureEnabled } = useTenant();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('language', langCode);
    setLangDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // System Admin specific navigation
  const systemAdminNavigation = [
    { name: t('dashboard.dashboard'), path: '/admin', icon: Home, roles: ['system_admin'], feature: null },
    { name: t('settings.settings'), path: '/admin/system/settings', icon: Settings, roles: ['system_admin'], feature: null },
    { name: t('support.pricing'), path: '/admin/system/pricing', icon: Zap, roles: ['system_admin'], feature: null },
    { name: t('systemAdmin.tenants'), path: '/admin/system/tenants', icon: Shield, roles: ['system_admin'], feature: null },
    { name: t('systemAdmin.users'), path: '/admin/system/users', icon: Users, roles: ['system_admin'], feature: null },
    { name: t('support.title'), path: '/admin/system/support', icon: MessageCircle, roles: ['system_admin'], feature: null },
    { name: t('audit.title'), path: '/admin/system/audit', icon: BarChart3, roles: ['system_admin'], feature: null },
  ];

  // Note: tables, orders, inventory, loyalty are hidden for now (services kept for future)
  const regularNavigation = [
    { name: t('dashboard.dashboard'), path: '/admin', icon: Home, roles: ['admin', 'cashier', 'waiter'], feature: null },
    { name: t('menu.menuManagement'), path: '/admin/menu', icon: Utensils, roles: ['admin'], feature: null },
    // { name: t('tables.tableManagement'), path: '/admin/tables', icon: Grid3x3, roles: ['admin', 'waiter'], feature: 'tableManagement' },
    // { name: t('orders.orders'), path: '/admin/orders', icon: ShoppingCart, roles: ['admin', 'cashier', 'waiter'], feature: 'orders' },
    // { name: t('inventory.inventory'), path: '/admin/inventory', icon: Package, roles: ['admin'], feature: 'inventory' },
    // { name: t('loyalty.loyalty'), path: '/admin/loyalty', icon: Heart, roles: ['admin', 'cashier'], feature: 'loyalty' },
    { name: t('staff.staff'), path: '/admin/staff', icon: Users, roles: ['admin'], feature: 'staffManagement' },
    { name: t('analytics.title', 'Analytics'), path: '/admin/analytics', icon: PieChart, roles: ['admin'], feature: null },
    { name: t('addons.title', 'Add-ons'), path: '/admin/addons', icon: Puzzle, roles: ['admin'], feature: null },
    // { name: t('reports.reports'), path: '/admin/reports', icon: BarChart3, roles: ['admin'], feature: 'advancedReporting' },
    { name: 'Upgrade', path: '/admin/upgrade', icon: Zap, roles: ['admin'], feature: null, showOnlyForStarter: true },
    { name: t('support.title'), path: '/admin/support', icon: MessageCircle, roles: ['admin'], feature: null },
    { name: t('settings.settings'), path: '/admin/settings', icon: Settings, roles: ['admin'], feature: null },
  ];

  // Use system admin navigation for system_admin role, otherwise use regular navigation
  const navigation = user?.role === 'system_admin' ? systemAdminNavigation : regularNavigation;

  const filteredNav = navigation.filter(item => {
    const hasRole = item.roles.includes(user?.role);
    const hasFeature = item.feature === null || isFeatureEnabled(item.feature);
    const showForPlan = !item.showOnlyForStarter || (item.showOnlyForStarter && (!tenant?.plan || tenant?.plan === 'STARTER' || tenant?.plan === 'starter'));
    return hasRole && hasFeature && showForPlan;
  });

  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          {sidebarOpen ? (
            <img 
              src="/idemenu-logo.svg" 
              alt="idemenu" 
              className="sidebar-logo" 
              style={{ filter: isDarkMode ? 'invert(1) hue-rotate(180deg)' : 'none' }}
            />
          ) : (
            <button 
              className="sidebar-logo-btn"
              onClick={() => setSidebarOpen(true)} 
              data-testid="sidebar-logo-btn"
              title="Open menu"
              aria-label="Open menu"
            >
              <img 
                src="/logo192.png" 
                alt="logo" 
                className="sidebar-logo-icon" 
                style={{ filter: isDarkMode ? 'invert(1) hue-rotate(180deg)' : 'none' }}
              />
            </button>
          )}
          {sidebarOpen && (
            <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="sidebar-toggle-btn" title="Close menu">
              <X size={20} />
            </button>
          )}
        </div>

        <nav className="sidebar-nav">
          {filteredNav.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                title={!sidebarOpen ? item.name : ''}
              >
                <Icon size={20} className="nav-icon" />
                {sidebarOpen && <span className="nav-label">{item.name}</span>}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Mobile Menu Overlay */}
      {sidebarOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="main-content">
        {/* Subscription Warning Banner */}
        <SubscriptionBanner />
        
        {/* Header */}
        <header className="header">
          <div className="header-left">
            <button 
              className="mobile-menu-btn"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
              title="Open menu"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
            <h1 className="page-title">{filteredNav.find(n => n.path === location.pathname)?.name || t('dashboard.dashboard')}</h1>
          </div>
          <div className="header-right">
            <button 
              className="dark-mode-toggle" 
              onClick={toggleDarkMode}
              data-testid="dark-mode-toggle-btn"
              title={isDarkMode ? t('settings.lightMode') : t('settings.darkMode')}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <div className="lang-dropdown" ref={langDropdownRef}>
              <button 
                className="lang-toggle" 
                onClick={() => setLangDropdownOpen(!langDropdownOpen)} 
                data-testid="language-toggle-btn"
              >
                <span className="lang-flag">{currentLang.flag}</span>
                <span>{currentLang.code.toUpperCase()}</span>
                <ChevronDown size={14} className={`lang-chevron ${langDropdownOpen ? 'open' : ''}`} />
              </button>
              {langDropdownOpen && (
                <div className="lang-dropdown-menu">
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      className={`lang-option ${lang.code === i18n.language ? 'active' : ''}`}
                      onClick={() => changeLanguage(lang.code)}
                    >
                      <span className="lang-flag">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="user-menu">
              <div className="user-info">
                <span className="user-name" data-testid="user-name">{user?.username}</span>
                <span className="user-role" data-testid="user-role">{t(user?.role)}</span>
              </div>
              <button className="logout-btn" onClick={handleLogout} data-testid="logout-btn">
                <LogOut size={18} />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="page-content">
          <Outlet />
        </main>
      </div>

      {/* Cookie Banner for Admin */}
      <CookieBanner 
        context="admin" 
        language={i18n.language}
        t={t}
      />

      <style>{`
        .layout {
          display: flex;
          min-height: 100vh;
          background: hsl(var(--background));
          transition: background-color 0.3s ease;
        }

        .sidebar {
          width: 260px;
          background: hsl(var(--card));
          border-right: 1px solid hsl(var(--border));
          transition: width 0.3s ease, background-color 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .sidebar.closed {
          width: 80px;
        }

        .sidebar-header {
          padding: 20px 14px;
          border-bottom: 1px solid hsl(var(--border));
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .sidebar-logo {
          height: 40px;
          width: auto;
          transition: filter 0.3s ease;
        }

        /* Invert logo for dark mode */
        :global(.dark) .sidebar-logo {
          filter: invert(1) hue-rotate(180deg);
        }

        .sidebar-logo-btn {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 1;
          transition: opacity 0.2s;
        }

        .sidebar-logo-btn:hover {
          opacity: 0.8;
        }

        .sidebar-logo-icon {
          width: 44px;
          height: 44px;
          object-fit: contain;
          transition: filter 0.3s ease;
        }

        /* Invert logo icon for dark mode */
        :global(.dark) .sidebar-logo-icon {
          filter: invert(1) hue-rotate(180deg);
        }

        .sidebar-title {
          font-size: 20px;
          font-weight: 700;
          color: hsl(var(--foreground));
          font-family: 'Manrope', sans-serif;
        }

        .sidebar-toggle {
          background: none;
          padding: 8px;
          border-radius: 6px;
          color: hsl(var(--foreground));
          flex-shrink: 0;
          cursor: pointer;
          transition: background-color 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sidebar-toggle:hover {
          background: hsl(var(--muted));
        }

        .sidebar-nav {
          padding: 12px 8px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          border-radius: 8px;
          color: hsl(var(--foreground) / 0.6);
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s, color 0.2s;
          position: relative;
        }

        .sidebar.closed .nav-item {
          justify-content: center;
          padding: 12px;
          gap: 0;
        }

        .sidebar.closed .nav-label {
          display: none;
        }

        .nav-icon {
          flex-shrink: 0;
        }

        .nav-label {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .nav-item:hover {
          background: hsl(var(--muted));
          color: hsl(var(--foreground));
        }

        .nav-item.active {
          background: hsl(var(--primary));
          color: white;
        }

        .main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .header {
          background: hsl(var(--card));
          border-bottom: 1px solid hsl(var(--border));
          padding: 16px 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          transition: background-color 0.3s ease;
        }

        .page-title {
          font-size: 24px;
          font-weight: 600;
          color: hsl(var(--foreground));
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .dark-mode-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          background: hsl(var(--muted));
          border: none;
          border-radius: 10px;
          color: hsl(var(--foreground) / 0.7);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .dark-mode-toggle:hover {
          background: hsl(var(--muted) / 0.7);
          color: hsl(var(--foreground));
          transform: scale(1.05);
        }

        .dark-mode-toggle:active {
          transform: scale(0.95);
        }

        .lang-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: hsl(var(--muted));
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          color: hsl(var(--foreground) / 0.7);
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .lang-toggle:hover {
          background: hsl(var(--muted) / 0.8);
        }

        .lang-dropdown {
          position: relative;
        }

        .lang-flag {
          font-size: 16px;
          line-height: 1;
        }

        .lang-chevron {
          transition: transform 0.2s ease;
        }

        .lang-chevron.open {
          transform: rotate(180deg);
        }

        .lang-dropdown-menu {
          position: absolute;
          top: calc(100% + 4px);
          right: 0;
          min-width: 140px;
          background: hsl(var(--card));
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          overflow: hidden;
        }

        .lang-option {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 14px;
          background: none;
          border: none;
          font-size: 14px;
          color: hsl(var(--foreground));
          cursor: pointer;
          transition: background-color 0.15s;
          text-align: left;
        }

        .lang-option:hover {
          background: hsl(var(--muted));
        }

        .lang-option.active {
          background: hsl(var(--primary) / 0.1);
          color: hsl(var(--primary));
          font-weight: 600;
        }

        .user-menu {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .user-info {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }

        .user-name {
          font-size: 14px;
          font-weight: 600;
          color: hsl(var(--foreground));
        }

        .user-role {
          font-size: 12px;
          color: hsl(var(--foreground) / 0.6);
          text-transform: capitalize;
        }

        .logout-btn {
          padding: 10px;
          background: hsl(var(--destructive) / 0.1);
          color: hsl(var(--destructive));
          border-radius: 8px;
          border: none;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .logout-btn:hover {
          background: hsl(var(--destructive) / 0.2);
        }

        .page-content {
          flex: 1;
          padding: 32px;
          overflow-y: auto;
        }

        .mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          padding: 8px;
          cursor: pointer;
          color: hsl(var(--foreground));
          margin-right: 12px;
        }

        .mobile-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 99;
        }

        @media (max-width: 768px) {
          .sidebar {
            position: fixed;
            z-index: 101;
            height: 100vh;
            left: 0;
            top: 0;
            transition: transform 0.3s ease;
          }

          .sidebar.closed {
            transform: translateX(-100%);
            width: 260px;
          }

          .sidebar.open {
            transform: translateX(0);
          }

          .mobile-menu-btn {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .mobile-overlay {
            display: block;
          }

          .mobile-overlay.hidden {
            display: none;
          }

          .page-content {
            padding: 20px;
          }

          .header {
            padding: 16px 20px;
          }

          .header-left {
            display: flex;
            align-items: center;
            gap: 0;
          }

          .page-title {
            font-size: 18px;
          }
        }
      `}</style>
    </div>
  );
};

export default Layout;
